import store from '@shared/store/index';

import { computed } from "vue";
import { useUserAuth } from '@/Groups/Audioplayer/private/imports/externalStoreData';
import { formatSeconds_h_min_s } from '@shared/utils/time/secondFormated.js';

const { hasPaidAccount, isLoadingUserData } = useUserAuth();

//---------------------------------------------------------------------
/**
 * Список подкастов из хранилища.
 * @type {import('vue').ComputedRef<Object>}
 */
const podcastsList = computed(() => store.state.Audioplayer.list);
//---------------------------------------------------------------------

//---------------------------------------------------------------------
/**
 * Список 'других' подкастов из хранилища.
 * @type {import('vue').ComputedRef<Object>}
 */
const otherPodcasts = computed(() => store.state.Audioplayer.otherPodcasts);
//---------------------------------------------------------------------

//---------------------------------------------------------------------
/**
 * Идет ли получение списка подкастов по эндпоинту
 * @type {import('vue').ComputedRef<Object>}
 */
const podcastsOtherListLoading = computed(() => store.state.Audioplayer.isLoadingPodcastsList);
//---------------------------------------------------------------------

//---------------------------------------------------------------------
/**
 * Текущий активный подкаст из хранилища.
 * @type {import('vue').ComputedRef<Object>}
 */
const podcast = computed(() => podcastsList.value[store.state.Audioplayer.activePodcastId]);
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Флаг, указывающий, воспроизводится ли аудио в данный момент.
 * @type {import('vue').ComputedRef<boolean>}
 */
const isPlaying = computed(() => store.state.Audioplayer.isPlaying);
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Текущая скорость воспроизведения (1.0 = нормальная скорость).
 * @type {import('vue').ComputedRef<number>}
 */
const speed = computed(() => store.state.Audioplayer.speed);
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Флаг, указывающий, открыта ли sticky-панель плеера.
 * @type {import('vue').ComputedRef<boolean>}
 */
const isPanelOpen = computed(() => store.state.Audioplayer.isStickyPanelOpened);
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Флаг, указывающий, инициализирован и готов ли плеер к работе.
 * @type {import('vue').ComputedRef<boolean>}
 */
const isReady = computed(() => store.state.Audioplayer.isAudioplayerReady);
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Флаг инициализации плеера (загрузка метаданных)
 * Использовать при:
 * - Первоначальной загрузке плеера
 * - Смене подкаста
 */
const isInitializing = computed(() => store.state.Audioplayer.isAudioplayerLoading);
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Флаг буферизации аудио (загрузка аудиофайла)
 * Использовать при:
 * - Начале воспроизведения
 * - Перемотке
 * - Смене точки воспроизведения
 */
const isAudioBuffering = computed(() => store.state.Audioplayer.isLoading);
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Композитный флаг любой загрузки
 * Использовать для общего индикатора загрузки
 */
const isAnyLoading = computed(() => isInitializing.value || isAudioBuffering.value);
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Название подкаста с учетом прав пользователя
 */
const podcastName = computed(() => {
  const ctx = store.state.Audioplayer;
  const shouldShowFullTitle = hasPaidAccount.value && !isLoadingUserData.value;
  
  return shouldShowFullTitle 
    ? ctx.list[ctx.activePodcastId]?.title ?? ''
    : ctx.podcastPreviewText;
});
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Текущее время воспроизведения в секундах
 * @type {import('vue').ComputedRef<string>}
 */
const currentTime = computed(() => {
  return store.state.Audioplayer.currentTime;
});
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Текущее время воспроизведения в формате MM:SS
 * @type {import('vue').ComputedRef<string>}
 */
const currentTimeFormat = computed(() => {
  const ctx = store.state.Audioplayer;
  return formatSeconds_h_min_s(ctx.currentTime);
});
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Полная продолжительность аудио в формате MM:SS
 * @type {import('vue').ComputedRef<string>}
 */
const durationTimeAudioFormat = computed(() => {
  const ctx = store.state.Audioplayer;
  const podcast = ctx.list[ctx.activePodcastId];
  return podcast ? formatSeconds_h_min_s(podcast.audio_length) : '00:00';
});
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * ID текущего проигрываемого таймкода (в секундах)
 * @type {import('vue').ComputedRef<number|null>}
 */
const currentTimecodeId = computed(() => {
  if (!isPlaying.value || !podcast.value?.timecodes) return 0;
  const timecodes = Object.keys(podcast.value.timecodes).map(Number).sort((a, b) => a - b);
  for (let i = timecodes.length - 1; i >= 0; i--) {
    if (currentTime.value >= timecodes[i]) {
      return timecodes[i];
    }
  }
  return 0;
});
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Оставшееся время воспроизведения в секундах
 * @type {import('vue').ComputedRef<number>}
 */
const remainingTime = computed(() => {
  const podcast = store.state.Audioplayer.list[store.state.Audioplayer.activePodcastId];
  if (!podcast?.audio_length) return 0;
  return Math.max(0, podcast.audio_length - store.state.Audioplayer.currentTime);
});
//---------------------------------------------------------------------


//---------------------------------------------------------------------
/**
 * Оставшееся время воспроизведения в формате MM:SS
 * @type {import('vue').ComputedRef<string>}
 */
const remainingTimeFormat = computed(() => {
  return formatSeconds_h_min_s(remainingTime.value);
});
//---------------------------------------------------------------------


/**
 * Composable-функция для работы с состоянием аудиоплеера.
 * Предоставляет реактивные свойства состояния плеера и текущего подкаста.
 * 
 * @example
 * const { isPlaying, currentTimePlaying } = useAudioPlayerState();
 * 
 * @returns {{
 *   podcastsList: import('vue').ComputedRef<Object>, // Полные список подкастов
 *   podcastsOtherListLoading: import('vue').ComputedRef<boolean>, // true/false - идет ли подгрузка списка подкастов по эндпоинту
 *   otherPodcasts: import('vue').ComputedRef<Object>, // Список других подкастов
 *   podcast: import('vue').ComputedRef<Object>, // Полные данные текущего подкаста
 *   podcastName: import('vue').ComputedRef<string>, // Название подкаста (учитывает подписку)
 *   isPlaying: import('vue').ComputedRef<boolean>, // true/false - играет ли аудио
 *   isInitializing: import('vue').ComputedRef<boolean>, // Загрузка при инициализации
 *   isAudioBuffering: import('vue').ComputedRef<boolean>, // Буферизация аудио
 *   isAnyLoading: import('vue').ComputedRef<boolean>, // Любой тип загрузки
 *   speed: import('vue').ComputedRef<number>, // Скорость воспроизведения (1.0, 1.5 и т.д.)
 *   isPanelOpen: import('vue').ComputedRef<boolean>, // Видимость sticky-панели
 *   isReady: import('vue').ComputedRef<boolean>, // Флаг готовности плеера
 *   currentTime: import('vue').ComputedRef<number>, //Время воспроизведения аудио в секундах
 *   currentTimeFormat: import('vue').ComputedRef<string>, // Текущее время "MM:SS"
 *   durationTimeAudioFormat: import('vue').ComputedRef<string> // Полная длительность "MM:SS"
 *   currentTimecodeId: import('vue').ComputedRef<number>, //ID проигрываемого таймкода
 *   remainingTime: import('vue').ComputedRef<number>, // Оставшееся время в секундах
 *   remainingTimeFormat: import('vue').ComputedRef<string> // Оставшееся время "MM:SS"
 * }}
 */


export function useAudioPlayerState() {
  return {
    podcast,
    podcastName,
    isPlaying,
    isInitializing,
    isAudioBuffering,
    isAnyLoading,
    speed,
    isPanelOpen,
    isReady,
    currentTime,
    currentTimeFormat,
    durationTimeAudioFormat,
    currentTimecodeId,
    remainingTime,
    remainingTimeFormat,
    podcastsList,
    otherPodcasts,
    podcastsOtherListLoading,
  };
}